import { Route, Routes } from 'react-router';
import { RequireAuth } from '@/auth/RequireAuth';
import { FeedbackButton } from '@/features/feedback/FeedbackButton';
import { FeedbackDialog } from '@/features/feedback/FeedbackDialog';
import { openFeedback, useTrackFeedbackRoute } from '@/features/feedback/store';
import { useApplyReading } from '@/features/settings/reading';
import { ScreenI18nProvider } from '@/i18n/I18nProvider';
import { HomeRoute } from '@/routes/HomeRoute';
import { LeaderboardRoute } from '@/routes/LeaderboardRoute';
import { LoginRoute } from '@/routes/LoginRoute';
import { NewStoryRoute } from '@/routes/NewStoryRoute';
import { NotFoundRoute } from '@/routes/NotFoundRoute';
import { PlayRoute } from '@/routes/PlayRoute';
import { SettingsRoute } from '@/routes/SettingsRoute';
import { StoriesRoute } from '@/routes/StoriesRoute';
import { ErrorBoundary } from '@/ui/ErrorBoundary';
import { OfflineNotice } from '@/ui/OfflineNotice';
import { ToastHost } from '@/ui/ToastHost';

/**
 * Declarative routing. Seven routes, no data router, no route-level code splitting: the
 * peripheral surfaces are three small pages and two dialogs, and dialogs stay
 * dialogs.
 *
 * The split is the guard (D8): `/login` and `/leaderboard` are readable without a
 * session — the leaderboard is public, same-origin JSON and a player should be able to
 * read it before deciding to sign in. Everything else sits behind `RequireAuth`.
 *
 * **The four siblings beside the routes are not decoration.** The error boundary wraps only
 * the routes, so a render crash inside one still leaves the toast host, the feedback button
 * and the feedback dialog mounted — which is what lets the crash screen offer *report this*
 * and lets that report carry the error's own message. A boundary wrapped around all four
 * would take its own reporting affordance down with the page.
 */
export function App() {
  // The reading settings, pushed onto `<html>` once for the whole app rather than by
  // whichever screen happens to be mounted — `--measure` is read by every page, not only the
  // feed, so a preference that only applied inside `/play` would change the page width as the
  // player navigated.
  useApplyReading();

  // Here rather than in the button, because the button is hidden for a signed-out reader and
  // the route still has to be right for whatever opens the form next — and because this sits
  // outside the error boundary, so the path survives the crash it is about to be reported on.
  useTrackFeedbackRoute();

  return (
    <>
      <ErrorBoundary
        onReport={(error) => {
          openFeedback({ category: 'bug', prefill: `The interface crashed: ${error.message}` });
        }}
      >
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/leaderboard" element={<LeaderboardRoute />} />

          <Route element={<RequireAuth />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/stories" element={<StoriesRoute />} />
            <Route path="/stories/new" element={<NewStoryRoute />} />
            <Route path="/settings" element={<SettingsRoute />} />
            <Route path="/play/:storyId" element={<PlayRoute />} />
          </Route>

          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </ErrorBoundary>

      {/* The chrome renders in the language of the *screen*, not of the profile: it floats
          over the story and would otherwise be the one English corner of a `ua` story
          (`ScreenI18nProvider`). Inside the router and outside the boundary, so a route
          crash still leaves the report button — and its label — standing. */}
      <ScreenI18nProvider>
        <FeedbackButton />
        <FeedbackDialog />
        <ToastHost />
        {/* Outside the boundary with the other two, for the same reason: a crash must not
            take down the thing that explains why the page cannot reach the server. */}
        <OfflineNotice />
      </ScreenI18nProvider>
    </>
  );
}
